/**
 * Наряд-допуск (КЧ-05): вид работ, срок, бригада с проверкой допусков (КЧ-07), маршрут подписей, ознакомление
 * бригады, анализ воздуха, остановка и возобновление работ, изоляции LOTO, одновременные работы, продление,
 * завершение работ, журнал, открытие и закрытие.
 */
import { CREDENTIAL_KINDS, type CredentialKind, PERMIT_KINDS, type PermitDetail } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { CheckCircle2, Circle, Printer } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel } from '../../ui/layout';
import { PermitGasPanel, PermitJournal } from './PermitAir';
import { PermitHashNote, PermitRisksPanel } from './PermitJsa';
import { ExtendDialog, PermitIsolationsPanel, PermitSimopsPanel } from './PermitLoto';
import { PermitBadge, ReasonDialog, useSafetyWorkers } from './safetyShared';

export function PermitPage() {
  const { permitId } = useParams({ strict: false }) as { permitId: string };
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['qa-permit', permitId],
    queryFn: () => api<PermitDetail>(`/quality/permits/${permitId}`),
  });
  useCrumbs(q.data ? [{ label: q.data.number, title: q.data.title }] : null, q.error);
  const refresh = (d?: PermitDetail) => {
    if (d) qc.setQueryData(['qa-permit', permitId], d);
    else qc.invalidateQueries({ queryKey: ['qa-permit', permitId] });
    qc.invalidateQueries({ queryKey: ['qa-permits'] });
    qc.invalidateQueries({ queryKey: ['inbox'] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
  };
  const act = useMutation({
    mutationFn: (a: { path: string; body?: unknown; done?: string }) =>
      api<PermitDetail>(`/quality/permits/${permitId}/${a.path}`, { body: a.body ?? {} }).then((d) => ({
        d,
        done: a.done,
      })),
    onSuccess: ({ d, done }) => {
      refresh(d);
      if (done) toast(done);
      setAsk(null);
    },
  });
  const [ask, setAsk] = useState<null | 'return' | 'cancel' | 'close' | 'suspend' | 'complete'>(null);
  const [extend, setExtend] = useState(false);
  const [worker, setWorker] = useState('');
  const workers = useSafetyWorkers(q.data?.projectId);
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const d = q.data;
  const inCrew = new Set(d.crew.map((c) => c.workerId));
  const canReturn = d.status === 'signing' || d.status === 'signed';
  return (
    <Page>
      <PageHeader
        title={`${d.number}: ${d.title}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <PermitBadge status={d.status} expired={d.expired} />
            <span>{PERMIT_KINDS[d.kind].title}</span>
          </span>
        }
        actions={
          <>
            <a
              href={`/api/v1/quality/permits/${d.id}/print`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-11 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] font-medium text-ink hover:underline"
            >
              <Printer className="size-4" aria-hidden />
              Печатная форма
            </a>
            {d.can.cancel ? (
              <Button variant="ghost" size="lg" onClick={() => setAsk('cancel')}>
                Отменить
              </Button>
            ) : null}
            {canReturn && (d.can.sign || d.can.edit || d.can.cancel) ? (
              <Button variant="secondary" size="lg" onClick={() => setAsk('return')}>
                Вернуть в работу
              </Button>
            ) : null}
            {d.can.submit ? (
              <Button
                variant="primary"
                size="lg"
                loading={act.isPending}
                onClick={() => act.mutate({ path: 'submit', done: 'Наряд ушёл на подпись' })}
              >
                Отправить на подпись
              </Button>
            ) : null}
            {d.can.sign ? (
              <Button
                variant="primary"
                size="lg"
                loading={act.isPending}
                onClick={() => act.mutate({ path: 'sign', done: 'Подписано' })}
              >
                Подписать
              </Button>
            ) : null}
            {d.status === 'signed' && d.can.open ? (
              <Button
                variant="primary"
                size="lg"
                loading={act.isPending}
                onClick={() => act.mutate({ path: 'open', done: 'Наряд действует' })}
              >
                Открыть наряд
              </Button>
            ) : null}
            {d.can.suspend ? (
              <Button variant="danger" size="lg" onClick={() => setAsk('suspend')}>
                Остановить работы
              </Button>
            ) : null}
            {d.can.resume ? (
              <Button
                variant="primary"
                size="lg"
                loading={act.isPending}
                disabled={d.resumeBlockers.length > 0}
                onClick={() => act.mutate({ path: 'resume', body: {}, done: 'Работы возобновлены' })}
              >
                Возобновить работы
              </Button>
            ) : null}
            {d.can.extend ? (
              <Button variant="secondary" size="lg" onClick={() => setExtend(true)}>
                Продлить
              </Button>
            ) : null}
            {d.can.complete ? (
              <Button
                variant={d.status === 'active' ? 'primary' : 'secondary'}
                size="lg"
                onClick={() => setAsk('complete')}
              >
                Работы завершены
              </Button>
            ) : null}
            {d.can.close ? (
              <Button
                variant={d.status === 'done' ? 'primary' : 'secondary'}
                size="lg"
                disabled={d.closeBlockers.length > 0}
                onClick={() => setAsk('close')}
              >
                Закрыть наряд
              </Button>
            ) : null}
          </>
        }
      />
      {act.isError ? <ErrorBox error={act.error} className="mb-4" /> : null}
      {d.expired ? (
        <p
          className="mb-4 rounded-md border border-danger bg-surface-2 p-3 text-[14px] text-ink"
          role="alert"
        >
          Срок наряда вышел: он больше не даёт права работать. Закройте его и выдайте новый.
        </p>
      ) : null}
      {d.status === 'suspended' ? (
        <div
          className="mb-4 rounded-md border border-danger bg-surface-2 p-3 text-[14px] text-ink"
          role="alert"
        >
          <p className="font-semibold">
            Работы остановлены{d.suspendedAt ? ` ${dateTime(d.suspendedAt)}` : ''}: работать по наряду нельзя.
          </p>
          {d.suspendReason ? <p className="[overflow-wrap:anywhere]">{d.suspendReason}</p> : null}
          {d.resumeBlockers.length ? (
            <>
              <p className="mt-2">Чтобы возобновить:</p>
              <ul className="list-disc pl-5">
                {d.resumeBlockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
      {d.blockers.length && d.status !== 'draft' ? (
        <Panel title="Чтобы открыть наряд" className="mb-6">
          <ul className="list-disc pl-5 text-[14px] text-ink">
            {d.blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </Panel>
      ) : null}
      <Panel className="mb-6">
        <KeyValue
          items={[
            {
              label: 'Проект',
              value: (
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: d.projectId }}
                  className="inline-flex min-h-11 items-center underline md:min-h-0"
                >
                  {d.projectCode}
                </Link>
              ),
            },
            { label: 'Объект и место', value: [d.objectName, d.place].filter(Boolean).join(', ') || '—' },
            { label: 'Подрядчик', value: d.contractorName ?? 'Свои силы' },
            { label: 'Действует', value: `${dateTime(d.validFrom)} — ${dateTime(d.validUntil)}` },
            { label: 'Меры безопасности', value: d.measures ?? '—' },
            {
              label: 'Нужны допуски',
              value: d.requires.length
                ? d.requires.map((k) => CREDENTIAL_KINDS[k as CredentialKind]).join(', ')
                : 'Особых нет',
            },
            ...(d.openedAt ? [{ label: 'Открыт', value: dateTime(d.openedAt) }] : []),
            ...(d.closedAt
              ? [
                  {
                    label: d.status === 'cancelled' ? 'Отменён' : 'Закрыт',
                    value: `${dateTime(d.closedAt)}${d.closeNote ? `: ${d.closeNote}` : ''}${d.cancelReason ? `: ${d.cancelReason}` : ''}`,
                  },
                ]
              : []),
          ]}
        />
      </Panel>
      {d.closeBlockers.length ? (
        <Panel
          title={d.status === 'cancelled' ? 'Изоляции не сняты' : 'Чтобы закрыть наряд'}
          className="mb-6"
        >
          <ul className="list-disc pl-5 text-[14px] text-ink">
            {d.closeBlockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </Panel>
      ) : null}
      <PermitSimopsPanel d={d} onDone={refresh} />
      <PermitRisksPanel d={d} onDone={refresh} />
      <PermitIsolationsPanel d={d} onDone={refresh} />
      <PermitGasPanel d={d} onDone={refresh} />
      <Panel title="Маршрут подписей" className="mb-6">
        {d.signs.length ? (
          <ol className="flex flex-col gap-2">
            {d.signs.map((s) => (
              <li key={s.step} className="flex items-center gap-2 text-[14px] text-ink">
                {s.signedAt ? (
                  <CheckCircle2 className="size-5 text-success" aria-hidden />
                ) : (
                  <Circle className="size-5 text-ink-3" aria-hidden />
                )}
                <span className="[overflow-wrap:anywhere]">
                  {s.role === 'responsible'
                    ? 'Ответственный руководитель работ'
                    : s.role === 'approver'
                      ? 'Согласует охрана труда'
                      : 'Выдаёт наряд'}
                  : {s.userName}
                  {s.signedAt ? ` · ${dateTime(s.signedAt)}` : ''}
                  {s.docHash ? (
                    <span className="text-ink-2" title={s.docHash}>
                      {' '}
                      · отпечаток {s.docHash.slice(0, 12)}…
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-[14px] text-ink-2">
            Маршрут появится после отправки: {d.responsibleName}
            {d.approverName ? ` → ${d.approverName}` : ''} → {d.issuerName}.
          </p>
        )}
        <PermitHashNote d={d} />
      </Panel>
      <Panel title={`Бригада (${d.crew.length})`}>
        {d.status === 'draft' && d.can.edit ? (
          <div className="mb-4 flex flex-col gap-2 md:flex-row">
            <Select
              aria-label="Работник"
              className="h-11 md:h-10 md:w-96"
              value={worker}
              onChange={(e) => setWorker(e.target.value)}
            >
              <option value="">Добавить в бригаду…</option>
              {(workers.data ?? [])
                .filter((w) => !inCrew.has(w.id))
                .map((w) => {
                  const lacks = d.requires.filter((k) => !w.validKinds.includes(k as CredentialKind));
                  return (
                    <option key={w.id} value={w.id} disabled={lacks.length > 0}>
                      {w.fullName}
                      {lacks.length
                        ? ` — нет допуска: ${lacks.map((k) => CREDENTIAL_KINDS[k as CredentialKind]).join(', ')}`
                        : ''}
                    </option>
                  );
                })}
            </Select>
            <Button
              variant="secondary"
              className="h-11 md:h-10"
              disabled={!worker}
              loading={act.isPending}
              onClick={() => {
                act.mutate({ path: 'crew', body: { workerId: worker } });
                setWorker('');
              }}
            >
              Добавить
            </Button>
          </div>
        ) : null}
        {!d.crew.length ? (
          <p className="text-[14px] text-ink-2">
            В бригаде никого нет. Ставить можно только тех, у кого действует нужный допуск на весь срок
            наряда.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {d.crew.map((c) => (
              <li
                key={c.workerId}
                className="flex flex-col gap-2 rounded-md border border-line p-3 md:flex-row md:items-center"
              >
                <span className="min-w-0 flex-1 text-[14px] text-ink [overflow-wrap:anywhere]">
                  {c.fullName}
                  {c.counterpartyName ? ` · ${c.counterpartyName}` : ''}
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  {c.missing.length ? (
                    <Badge tone="danger">
                      Нет допуска: {c.missing.map((k) => CREDENTIAL_KINDS[k]).join(', ')}
                    </Badge>
                  ) : null}
                  {c.acknowledgedAt ? (
                    <Badge tone="success">
                      Ознакомлен{c.paper ? ' (бумага)' : ''} · {dateTime(c.acknowledgedAt)}
                    </Badge>
                  ) : d.status === 'signing' || d.status === 'signed' ? (
                    <>
                      <Badge tone="warning">Не ознакомлен</Badge>
                      {c.isMe ? (
                        <Button
                          className="h-11 md:h-10"
                          variant="primary"
                          loading={act.isPending}
                          onClick={() => act.mutate({ path: 'ack', body: {}, done: 'Ознакомление отмечено' })}
                        >
                          Ознакомлен
                        </Button>
                      ) : d.can.cancel ? (
                        <Button
                          className="h-11 md:h-10"
                          variant="secondary"
                          loading={act.isPending}
                          onClick={() =>
                            act.mutate({
                              path: 'ack',
                              body: { workerId: c.workerId },
                              done: 'Бумажная подпись отмечена',
                            })
                          }
                        >
                          Подписал на бумаге
                        </Button>
                      ) : null}
                    </>
                  ) : null}
                  {d.status === 'draft' && d.can.edit ? (
                    <button
                      type="button"
                      className="inline-flex min-h-11 items-center px-2 text-[13px] text-danger hover:underline md:min-h-0"
                      onClick={() =>
                        api<PermitDetail>(`/quality/permits/${permitId}/crew/${c.workerId}`, {
                          method: 'DELETE',
                        })
                          .then(refresh)
                          .catch((e) => toast(e instanceof Error ? e.message : 'Не удалось убрать', 'danger'))
                      }
                    >
                      Убрать
                    </button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {d.briefings.length ? (
        <Panel title="Инструктажи по наряду" className="mt-6">
          <ul className="flex flex-col gap-1">
            {d.briefings.map((b) => (
              <li key={b.id}>
                <Link
                  to="/qhse/briefings/$briefingId"
                  params={{ briefingId: b.id }}
                  className="inline-flex min-h-11 items-center underline md:min-h-0"
                >
                  {b.number} от {b.heldOn}
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
      <PermitJournal d={d} />
      {ask ? (
        <ReasonDialog
          title={
            ask === 'return'
              ? 'Вернуть наряд в работу'
              : ask === 'cancel'
                ? 'Отменить наряд'
                : ask === 'suspend'
                  ? 'Остановить работы'
                  : ask === 'complete'
                    ? 'Работы завершены, бригада выведена'
                    : 'Закрыть наряд'
          }
          label={ask === 'close' || ask === 'complete' ? 'Что сделано (по желанию)' : 'Причина'}
          confirm={
            ask === 'return'
              ? 'Вернуть'
              : ask === 'cancel'
                ? 'Отменить наряд'
                : ask === 'suspend'
                  ? 'Остановить'
                  : ask === 'complete'
                    ? 'Отметить'
                    : 'Закрыть'
          }
          required={ask !== 'close' && ask !== 'complete'}
          busy={act.isPending}
          error={act.error}
          onCancel={() => setAsk(null)}
          onConfirm={(t) =>
            act.mutate(
              ask === 'close'
                ? { path: 'close', body: { note: t || null }, done: 'Наряд закрыт' }
                : ask === 'complete'
                  ? { path: 'complete', body: { note: t || null }, done: 'Работы завершены' }
                  : {
                      path: ask,
                      body: { reason: t },
                      done:
                        ask === 'return'
                          ? 'Наряд возвращён в работу'
                          : ask === 'suspend'
                            ? 'Работы остановлены'
                            : 'Наряд отменён',
                    },
            )
          }
        />
      ) : null}
      {extend ? <ExtendDialog d={d} onDone={refresh} onClose={() => setExtend(false)} /> : null}
    </Page>
  );
}
